import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { balanceLabel, getToken, money, phoneFmt } from '../api';

type Incoming = {
  callId: string;
  phone: string;
  isNew: boolean;
  openOrders: number;
  client: {
    id: string;
    code: string;
    name: string;
    cachedBalanceKop: string;
    cachedBottles: number;
    comment: string | null;
    addresses: Array<{ street: string; house: string; flat: string | null }>;
  } | null;
  lastOrder: { number: string; deliveryDate: string; totalKop: string } | null;
};

/**
 * Карточка звонящего.
 *
 * Соединение держится через SSE (`/api/calls/stream`): опрос раз в несколько
 * секунд для этой задачи не годится — разговор закончится раньше, чем оператор
 * увидит, кто звонит.
 *
 * EventSource не умеет слать заголовок Authorization, поэтому токен уходит
 * параметром строки запроса; сервер принимает оба способа.
 */
export function CallPop() {
  const [call, setCall] = useState<Incoming | null>(null);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const es = new EventSource(`/api/calls/stream?token=${encodeURIComponent(token)}`);
    es.addEventListener('incoming', (e) => {
      try {
        setCall(JSON.parse((e as MessageEvent).data));
      } catch {
        /* битое событие — молча пропускаем, звонок важнее разбора */
      }
    });
    return () => es.close();
  }, []);

  if (!call) return null;
  const b = call.client ? balanceLabel(call.client.cachedBalanceKop) : null;
  const addr = call.client?.addresses?.[0];

  return (
    <div className="callpop">
      <div className="spread">
        <div className="ring">● Входящий звонок</div>
        <button className="ghost" onClick={() => setCall(null)} aria-label="Закрыть">
          ✕
        </button>
      </div>

      <div style={{ fontSize: 18, fontWeight: 600, margin: '6px 0' }}>{phoneFmt(call.phone)}</div>

      {call.client ? (
        <>
          <div style={{ fontSize: 16 }}>{call.client.name}</div>
          <div className="muted">{call.client.code}</div>
          <div className="row" style={{ margin: '10px 0', gap: 16 }}>
            <div>
              <div className="muted">Баланс</div>
              <div className={b?.kind === 'debt' ? 'debt' : b?.kind === 'prepaid' ? 'prepaid' : ''}>
                {b?.text}
              </div>
            </div>
            <div>
              <div className="muted">Тара</div>
              <div>{call.client.cachedBottles} шт.</div>
            </div>
          </div>
          {addr ? (
            <div className="muted">
              {addr.street}, {addr.house}
              {addr.flat ? `, ${addr.flat}` : ''}
            </div>
          ) : null}
          {call.client.comment ? <div className="badge amber" style={{ marginTop: 6 }}>{call.client.comment}</div> : null}
          {call.lastOrder ? (
            <div className="muted" style={{ marginTop: 8 }}>
              Последний заказ {call.lastOrder.number} на {money(call.lastOrder.totalKop)}
            </div>
          ) : null}
          {call.openOrders > 0 ? (
            <div className="badge blue" style={{ marginTop: 6 }}>Открытых заказов: {call.openOrders}</div>
          ) : null}
          <Link className="btn primary" style={{ width: '100%', marginTop: 12 }} to={`/clients/${call.client.id}`}>
            Открыть карточку
          </Link>
        </>
      ) : (
        <>
          <div className="badge amber">Номера нет в базе</div>
          <Link
            className="btn primary"
            style={{ width: '100%', marginTop: 12 }}
            to={`/clients?new=${call.phone}`}
          >
            Завести клиента
          </Link>
        </>
      )}
    </div>
  );
}
